import { Address4, Address6 } from 'ip-address';
import { calc } from './utils';

/**
 * 将数字转换为二进制字符串，并在左侧补零，使其总长度为8位
 * @param n 数字
 * @returns 二进制字符串
 */
export const toBinaryPad = n => {
  return n.toString(2).padStart(8, '0');
};

/**
 * 将数组中的每个元素转换为二进制字符串，并拼接在一起
 * @param arr 数组
 * @returns 拼接后的二进制字符串
 */
export const arrayToBinaryPad = arr => {
  return arr.map(n => toBinaryPad(+n)).join('');
};

/**
 * 将二进制字符串转换为IPv4地址
 * @param binary 二进制字符串
 * @returns IPv4地址
 */
export const binary2ip = binary => {
  return [0, 1, 2, 3].map(i => parseInt(binary.slice(calc.mul(i, 8), calc.mul(i + 1, 8)), 2)).join('.');
};

/**
 * 判断是否为有效的IPv4地址
 * @param ip IPv4地址
 * @param isSubnet 是否为子网地址
 * @returns 如果是有效的IPv4地址，则返回Address4对象；否则返回undefined
 */
export const isIpv4Address = (ip, isSubnet) => {
  if (!ip || !Address4.isValid(ip)) {
    return undefined;
  }
  if (isSubnet) {
    const [ipStr, maskStr] = ip.split('/');
    const mask = maskStr ? Number(maskStr) : NaN;
    if (isNaN(mask) || mask < 0 || mask > 32) {
      return undefined;
    }
    if (isSubnet === 'strict') {
      const binaryZeroPad = arrayToBinaryPad(ipStr.split('.'));
      if (!binaryZeroPad.endsWith('0'.repeat(32 - mask))) {
        return undefined;
      }
    }
  } else if (ip.includes('/')) {
    return undefined;
  }
  return new Address4(ip);
};

/**
 * 判断是否为有效的IPv6地址
 * @param ip IPv6地址
 * @param isSubnet 是否为子网地址
 * @returns 如果是有效的IPv6地址，则返回Address6对象；否则返回undefined
 */
export const isIpv6Address = (ip, isSubnet) => {
  if (ip && !!isSubnet === ip.includes('/') && Address6.isValid(ip)) {
    return new Address6(ip);
  }
  return undefined;
};

/**
 * 校正IP地址的格式
 * @param ip IP地址
 * @returns 校正后的IP地址
 */
export const correctIp = ip => {
  if (ip && !ip.includes('/')) {
    if (Address4.isValid(ip)) {
      return new Address4(ip).correctForm();
    }
    if (Address6.isValid(ip)) {
      return new Address6(ip).correctForm();
    }
  }
  return ip;
};

/**
 * 判断两个IP地址是否相同
 * @param ipA IP地址A
 * @param ipB IP地址B
 * @returns 如果两个IP地址相同，则返回true；否则返回false
 */
export const isSameIp = (ipA, ipB) => {
  const a = correctIp(ipA);
  if (!a) {
    return ipA === ipB;
  }
  const b = correctIp(ipB);
  if (!b) {
    return ipA === ipB;
  }
  return a === b;
};

/**
 * 判断是否为网络地址
 * @param ip IP地址
 * @returns 如果是网络地址，则返回true；否则返回false
 */
export const isNetworkAddress = ip => {
  const address = isIpv4Address(ip);
  if (address) {
    return address.parsedAddress.join('.') === address.startAddress().parsedAddress.join('.');
  }
  return false;
};

/**
 * 判断是否为广播地址
 * @param ip IP地址
 * @returns 如果是广播地址，则返回true；否则返回false
 */
export const isBroadcastAddress = ip => {
  const address = isIpv4Address(ip);
  if (address) {
    return address.parsedAddress.join('.') === address.endAddress().parsedAddress.join('.');
  }
  return false;
};

/**
 * 判断是否为网关地址
 * @param ip IP地址
 * @returns 如果是网关地址，则返回true；否则返回false
 */
export const isGatewayAddress = ip => {
  const address = isIpv4Address(ip);
  if (address) {
    const {
      parsedAddress,
      subnet
    } = address;
    const z = Number(parsedAddress[3]);
    if (z > 0) {
      parsedAddress[3] = (z - 1).toString();
      return isNetworkAddress(parsedAddress.join('.') + subnet);
    }
  }
  return false;
};

/**
 * 生成n个指定网段内的IPv6地址
 * @param cidr 网段
 * @param count 个数（最多支持65536）
 * @param excludes 排除的IP地址
 * @returns 生成的IPv6地址数组
 */
export const generateIPv6Addresses = function (cidr) {
  let count = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 1;
  let excludes = arguments.length > 2 ? arguments[2] : undefined;
  try {
    const prefix = cidr.split('/')[0].split(':').slice(0, -1).join(':');
    const addresses = [];
    let i = 1;
    while (addresses.length < count) {
      const address = [prefix, i.toString(16)].join(':');
      i++;
      if (!(excludes !== null && excludes !== void 0 && excludes.includes(address))) {
        addresses.push(address);
      }
    }
    return addresses;
  } catch (error) {
    console.error(error);
    return [];
  }
};

/**
 * 生成n个指定网段内的IPv4地址
 * @param cidr  网段
 * @param count 个数
 * @param excludes 排除的IP地址
 * @param startIpv4 开始的IP地址
 * @returns 生成的IPv4地址数组
 */
export const generateIPv4Addresses = function (cidr) {
  let count = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 1;
  let excludes = arguments.length > 2 ? arguments[2] : undefined;
  let startIpv4 = arguments.length > 3 ? arguments[3] : undefined;
  try {
    var _excludes$map;
    const [subnet, bits] = cidr.split('/');
    const subnetParts = subnet.split('.').map(Number);
    const subnetInt = (subnetParts[0] << 24) + (subnetParts[1] << 16) + (subnetParts[2] << 8) + subnetParts[3];
    const mask = ~(2 ** (32 - Number(bits)) - 1);
    const excludeInts = (_excludes$map = excludes === null || excludes === void 0 ? void 0 : excludes.map(ip => {
      const parts = ip.split('.').map(Number);
      return (parts[0] << 24) + (parts[1] << 16) + (parts[2] << 8) + parts[3];
    })) !== null && _excludes$map !== void 0 ? _excludes$map : [];
    const startIpv4Int = startIpv4 ? (Number(startIpv4.split('.')[0]) << 24) + (Number(startIpv4.split('.')[1]) << 16) + (Number(startIpv4.split('.')[2]) << 8) + Number(startIpv4.split('.')[3]) : undefined;
    const addresses = [];
    for (let i = 1; addresses.length < count; i++) {
      const ipInt = (subnetInt & mask) + i;
      if (!excludeInts.includes(ipInt) && (!startIpv4 || startIpv4Int && ipInt >= startIpv4Int)) {
        const ipParts = [ipInt >>> 24 & 255, ipInt >>> 16 & 255, ipInt >>> 8 & 255, ipInt & 255];
        addresses.push(ipParts.join('.'));
      }
    }
    return addresses;
  } catch (error) {
    console.error(error);
    return [];
  }
};

/**
 * 判断两个IP段是否有交集
 * @param subnetA IP段A
 * @param subnetB IP段B
 * @returns 如果两个IP段有交集，则返回true；否则返回false
 */
export const whetherSubnetsConflict = (subnetA, subnetB) => {
  const addressA = isIpv4Address(subnetA, true);
  const addressB = isIpv4Address(subnetB, true);
  if (!addressA || !addressB) {
    throw new Error('Invalid subnet: ' + (addressA ? subnetB : subnetA));
  }
  const rangeA = {
    start: addressA.startAddress().bigInt(),
    end: addressA.endAddress().bigInt()
  };
  const rangeB = {
    start: addressB.startAddress().bigInt(),
    end: addressB.endAddress().bigInt()
  };
  return rangeA.start <= rangeB.end && rangeA.end >= rangeB.start;
};

/**
 * 计算一个IPv4网段内的总IP数量
 * @param {string | number} subnet - 子网，例如 "192.168.1.0/24" 或 "/24" 或 "24"
 * @returns {number} - 网段内的总IP数量
 */
export const getSubnetTotalIpCount = value => {
  const bits = typeof value === 'number' ? value : parseInt(value.includes('/') ? value.split('/')[1] : value, 10);
  if (isNaN(bits) || bits < 0 || bits > 32) {
    throw new Error('Invalid value: ' + value);
  }
  return 2 ** (32 - bits);
};